import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, ApiError } from '@/services/api'
import { Card, Button, Input, Modal, Badge } from '@/components/ui'
import { formatDate } from '@/utils/format-date'
import type { OrganizationRecipient } from '@spoofio/shared/types'

export function SettingsPage() {
  const queryClient = useQueryClient()
  const [newApiKey, setNewApiKey] = useState<string | null>(null)
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false)
  const [isAddRecipientModalOpen, setIsAddRecipientModalOpen] = useState(false)
  const [isEditOrgModalOpen, setIsEditOrgModalOpen] = useState(false)

  const { data: organizations, isLoading: orgsLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.listOrganizations(),
  })

  const org = organizations?.[0]

  const orgId = org?.id ?? ''

  const { data: orgDetails, isLoading: orgDetailsLoading } = useQuery({
    queryKey: ['organization', orgId],
    queryFn: () => api.getOrganization(orgId),
    enabled: !!orgId,
  })

  const { data: recipients } = useQuery({
    queryKey: ['organization-recipients', orgId],
    queryFn: () => api.listOrganizationRecipients(orgId),
    enabled: !!orgId,
  })

  const regenerateKeyMutation = useMutation({
    mutationFn: () => api.regenerateApiKey(orgId),
    onSuccess: (data) => {
      setNewApiKey(data.apiKey)
      setIsRegenerateModalOpen(false)
    },
  })

  const updateOrgMutation = useMutation({
    mutationFn: (data: { name?: string; graylogEndpoint?: string }) =>
      api.updateOrganization(orgId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organization', orgId] })
      void queryClient.invalidateQueries({ queryKey: ['organizations'] })
      setIsEditOrgModalOpen(false)
    },
  })

  const addRecipientMutation = useMutation({
    mutationFn: (email: string) =>
      api.addOrganizationRecipient(orgId, { email }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organization-recipients', orgId] })
      setIsAddRecipientModalOpen(false)
    },
  })

  const removeRecipientMutation = useMutation({
    mutationFn: (recipientId: string) =>
      api.removeOrganizationRecipient(recipientId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organization-recipients', orgId] })
    },
  })

  // Still loading organizations
  if (orgsLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-48" />
        <div className="bg-white shadow rounded-lg p-6 h-64" />
      </div>
    )
  }

  // No organizations exist
  if (!org) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <Card title="No Organization Found">
          <p className="text-gray-600 mb-4">
            You need to create an organization before you can configure settings.
          </p>
          <Link to="/canaries/new">
            <Button>Create Organization</Button>
          </Link>
        </Card>
      </div>
    )
  }

  // Loading organization details
  if (orgDetailsLoading || !orgDetails) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-48" />
        <div className="bg-white shadow rounded-lg p-6 h-64" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Settings</h1>

      {/* Organization Settings */}
      <Card
        title="Organization"
        description="Your organization settings"
        actions={
          <Button variant="secondary" size="sm" onClick={() => { setIsEditOrgModalOpen(true); }}>
            Edit
          </Button>
        }
      >
        <dl className="space-y-4">
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Name</dt>
            <dd className="text-sm font-medium">{orgDetails.name}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Slug</dt>
            <dd className="text-sm font-mono">{orgDetails.slug}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Total Canaries</dt>
            <dd className="text-sm font-medium">{orgDetails.canaryCount}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Detections (24h)</dt>
            <dd className="text-sm font-medium">{orgDetails.detectionCount24h}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Alerts (24h)</dt>
            <dd className="text-sm font-medium">{orgDetails.alertCount24h}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Created</dt>
            <dd className="text-sm">{formatDate(orgDetails.createdAt)}</dd>
          </div>
        </dl>
      </Card>

      {/* API Key */}
      <Card
        title="API Key"
        description="Use this key for programmatic access to the API"
      >
        {newApiKey ? (
          <div className="space-y-4">
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
              <p className="text-sm text-warning-800 font-medium mb-2">
                Save this API key - it won't be shown again!
              </p>
              <code className="block bg-white p-3 rounded border font-mono text-sm break-all">
                {newApiKey}
              </code>
            </div>
            <Button variant="secondary" onClick={() => { setNewApiKey(null); }}>
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <code className="bg-gray-50 px-3 py-2 rounded font-mono text-sm text-gray-500">
                pp_live_••••••••••••••••••••••••
              </code>
            </div>
            <p className="text-sm text-gray-500">
              For security, API keys are only shown once when created. Click Regenerate to create a new key.
            </p>
            <Button
              variant="danger"
              size="sm"
              onClick={() => { setIsRegenerateModalOpen(true); }}
            >
              Regenerate API Key
            </Button>
          </div>
        )}
      </Card>

      {/* Alert Recipients */}
      <Card
        title="Alert Recipients"
        description="Default email addresses that receive alerts for all canaries"
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => { setIsAddRecipientModalOpen(true); }}
          >
            Add Recipient
          </Button>
        }
      >
        <RecipientsList
          recipients={recipients ?? []}
          onRemove={(id) => { removeRecipientMutation.mutate(id); }}
          isRemoving={removeRecipientMutation.isPending}
        />
      </Card>

      {/* Graylog Integration */}
      <Card
        title="SIEM Integration"
        description="Send alerts to your Graylog/SIEM system"
      >
        {orgDetails.graylogEndpoint ? (
          <dl className="space-y-4">
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Endpoint</dt>
              <dd className="text-sm font-mono">{orgDetails.graylogEndpoint}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Status</dt>
              <dd>
                <Badge variant="success">Connected</Badge>
              </dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-gray-500">
            No SIEM integration configured. Edit organization settings to add a
            Graylog endpoint.
          </p>
        )}
      </Card>

      {/* Regenerate API Key Modal */}
      <Modal
        isOpen={isRegenerateModalOpen}
        onClose={() => { setIsRegenerateModalOpen(false); }}
        title="Regenerate API Key"
        footer={
          <>
            <Button
              variant="danger"
              onClick={() => { regenerateKeyMutation.mutate(); }}
              isLoading={regenerateKeyMutation.isPending}
            >
              Regenerate
            </Button>
            <Button
              variant="secondary"
              onClick={() => { setIsRegenerateModalOpen(false); }}
              className="mr-3"
            >
              Cancel
            </Button>
          </>
        }
      >
        <p className="text-gray-500">
          This will invalidate your current API key and generate a new one. Any
          integrations using the old key will stop working. Are you sure?
        </p>
      </Modal>

      {/* Edit Organization Modal */}
      <EditOrganizationModal
        isOpen={isEditOrgModalOpen}
        onClose={() => { setIsEditOrgModalOpen(false); }}
        organization={orgDetails}
        onSave={(data) => { updateOrgMutation.mutate(data); }}
        isLoading={updateOrgMutation.isPending}
      />

      {/* Add Recipient Modal */}
      <AddRecipientModal
        isOpen={isAddRecipientModalOpen}
        onClose={() => { setIsAddRecipientModalOpen(false); }}
        onAdd={(email) => { addRecipientMutation.mutate(email); }}
        isLoading={addRecipientMutation.isPending}
        error={
          addRecipientMutation.error instanceof ApiError
            ? addRecipientMutation.error.message
            : undefined
        }
      />
    </div>
  )
}

function RecipientsList({
  recipients,
  onRemove,
  isRemoving,
}: {
  recipients: OrganizationRecipient[]
  onRemove: (id: string) => void
  isRemoving: boolean
}) {
  if (recipients.length === 0) {
    return (
      <p className="text-sm text-gray-500 text-center py-4">
        No recipients configured. Add at least one email address to receive alerts.
      </p>
    )
  }

  return (
    <ul className="divide-y divide-gray-200">
      {recipients.map((recipient) => (
        <li key={recipient.id} className="flex items-center justify-between py-3">
          <span className="text-sm">{recipient.email}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { onRemove(recipient.id); }}
            disabled={isRemoving}
            className="text-danger-600 hover:text-danger-700"
          >
            Remove
          </Button>
        </li>
      ))}
    </ul>
  )
}

function EditOrganizationModal({
  isOpen,
  onClose,
  organization,
  onSave,
  isLoading,
}: {
  isOpen: boolean
  onClose: () => void
  organization: { name: string; graylogEndpoint?: string }
  onSave: (data: { name?: string; graylogEndpoint?: string }) => void
  isLoading: boolean
}) {
  const [name, setName] = useState(organization.name)
  const [graylogEndpoint, setGraylogEndpoint] = useState(
    organization.graylogEndpoint ?? ''
  )
  const [graylogError, setGraylogError] = useState<string | undefined>()

  // SECURITY: Validate Graylog URL format
  const validateGraylogUrl = (url: string): boolean => {
    if (!url) return true // Empty is OK (optional field)
    try {
      const parsed = new URL(url)
      // Must be https or http
      if (!['https:', 'http:'].includes(parsed.protocol)) {
        setGraylogError('URL must use http or https protocol')
        return false
      }
      // Must have a hostname
      if (!parsed.hostname) {
        setGraylogError('URL must have a valid hostname')
        return false
      }
      setGraylogError(undefined)
      return true
    } catch {
      setGraylogError('Please enter a valid URL')
      return false
    }
  }

  const handleGraylogChange = (value: string) => {
    setGraylogEndpoint(value)
    if (value) {
      validateGraylogUrl(value)
    } else {
      setGraylogError(undefined)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate before submit
    if (!validateGraylogUrl(graylogEndpoint)) {
      return
    }

    onSave({
      name: name !== organization.name ? name : undefined,
      graylogEndpoint:
        graylogEndpoint !== (organization.graylogEndpoint ?? '')
          ? graylogEndpoint || undefined
          : undefined,
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Organization"
      footer={
        <>
          <Button type="submit" form="edit-org-form" isLoading={isLoading}>
            Save Changes
          </Button>
          <Button variant="secondary" onClick={onClose} className="mr-3">
            Cancel
          </Button>
        </>
      }
    >
      <form id="edit-org-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Organization Name"
          value={name}
          onChange={(e) => { setName(e.target.value); }}
          required
        />
        <Input
          label="Graylog Endpoint (optional)"
          placeholder="https://graylog.example.com:12201/gelf"
          value={graylogEndpoint}
          onChange={(e) => { handleGraylogChange(e.target.value); }}
          error={graylogError}
          helperText="GELF HTTP input endpoint for SIEM integration"
        />
      </form>
    </Modal>
  )
}

function AddRecipientModal({
  isOpen,
  onClose,
  onAdd,
  isLoading,
  error,
}: {
  isOpen: boolean
  onClose: () => void
  onAdd: (email: string) => void
  isLoading: boolean
  error?: string
}) {
  const [email, setEmail] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onAdd(email.toLowerCase().trim())
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Alert Recipient"
      footer={
        <>
          <Button type="submit" form="add-recipient-form" isLoading={isLoading}>
            Add Recipient
          </Button>
          <Button variant="secondary" onClick={onClose} className="mr-3">
            Cancel
          </Button>
        </>
      }
    >
      <form id="add-recipient-form" onSubmit={handleSubmit}>
        <Input
          label="Email Address"
          type="email"
          placeholder="security@example.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); }}
          error={error}
          required
        />
      </form>
    </Modal>
  )
}

